import * as queryString from 'querystring';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Tooltip } from '@material-ui/core';

import { ApiParams } from '../../../common/api/api';
import TaskStatusDot from '../../../common/TaskStatusDot';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import {
  RenderColumnMenu,
  RenderOptions
} from '../../../common/trace-board/interfaces/TraceBoardColumn';
import TraceBoard from '../../../common/trace-board/TraceBoard';
import { TraceBoardContextState } from '../../../common/trace-board/TraceBoardContext';
import { Task } from '../../tasks/Task.interface';
import { useWorkflowContext } from '../../workflow/Workflow.context';
import { getStateNodeByStatus } from '../../workflow/Workflow.hooks';
import {
  Hazard,
  HazardCause,
  HazardControl
} from '../../hazards/Hazard.interface';
import styled from 'styled-components';
import { TraceBoardItem } from '../../../common/trace-board/interfaces/TraceBoardItem';
import { Person, WarningRounded } from '@material-ui/icons';
import {
  useCreateHazardAction,
  useCreateHazardCause,
  useCreateHazardControl,
  useGetProductHazardBoard,
  useUpdateHazard,
  useUpdateHazardAction,
  useUpdateHazardCause,
  useUpdateHazardControl
} from '../../hazards/Hazard.hooks';
import BoardColumnMenu from '../../tasks/board/BoardColumnMenu';
import { Product } from '../Product.interface';
import { getHazardIdPrefix } from '../../hazards/Hazard.config';
import { useCreateProductHazard } from '../Product.hooks';

const StyledSubContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px 12px;
  .assigned {
    display: flex;
    align-items: center;
    .icon-wrapper {
      display: flex;
      margin-right: 10px;
      height: 24px;
      width: 24px;
      border-radius: 12px;
      align-items: center;
      justify-content: center;
      background: #efefef;
      overflow: hidden;
      .icon {
        width: 18px;
        height: 18px;
      }
    }
  }
  .view-details {
    display: flex;
    flex-direction: column;
    .button {
      margin-top: 8px;
    }
  }
`;

const StyledWarning = styled(WarningRounded)`
  color: #f47560;
  width: 20px;
  height: 20px;
  margin-right: 4px;
`;
export interface ProductHazardBoardTraceProp {
  product: Product;
  apiParams?: ApiParams;
}

const ProductHazardBoardTrace: React.FC<ProductHazardBoardTraceProp> = ({
  product,
  apiParams
}) => {
  const { getWorkflow } = useWorkflowContext();
  const [hazardId, setHazardId] = useState<number>();
  const traceBoardRef = useRef<TraceBoardContextState>(null!);
  const hasFilters = Object.keys(apiParams || {}).length > 0;

  const { createdProductHazard, createProductHazard } = useCreateProductHazard(
    product.id
  );

  const { updatedHazard, updateHazard } = useUpdateHazard(hazardId || 0);

  const { createdHazardCause, createHazardCause } = useCreateHazardCause(
    hazardId || 0
  );

  const { updatedHazardCause, updateHazardCause } = useUpdateHazardCause(
    hazardId || 0
  );
  const { createdHazardControl, createHazardControl } = useCreateHazardControl(
    hazardId || 0
  );

  const { updatedHazardControl, updateHazardControl } = useUpdateHazardControl(
    hazardId || 0
  );
  const { createdHazardAction, createHazardAction } = useCreateHazardAction(
    hazardId || 0
  );

  const { updatedHazardAction, updateHazardAction } = useUpdateHazardAction(
    hazardId || 0
  );

  const {
    productHazardBoard: loadedHazardBoard,
    isLoading,
    getProductHazardBoard
  } = useGetProductHazardBoard(product.id || 0);
  useEffect(() => {
    getProductHazardBoard(apiParams);
    traceBoardRef.current.setLoadingItem(undefined);
    setHazardId(undefined);
  }, [
    apiParams,
    createdProductHazard,
    updatedHazard,
    createdHazardCause,
    updatedHazardCause,
    createdHazardControl,
    updatedHazardControl,
    createdHazardAction,
    updatedHazardAction,
    getProductHazardBoard
  ]);

  const renderColumnMenu = (
    { columnIndex, column }: RenderColumnMenu,
    onClose?: () => void,
    onRender?: () => void
  ) => {
    const taskEntities = column.items.map(item => item.data);
    return (
      <BoardColumnMenu
        taskEntities={taskEntities}
        onRender={onRender}
        onChange={() => {
          onClose?.();
          getProductHazardBoard(apiParams);
          traceBoardRef.current.setLoadingItem(undefined);
        }}
      />
    );
  };

  const countLinkedItemsRequiresReview = (
    item: TraceBoardItem,
    columnIndex: number
  ) => {
    return (
      item.connectors
        ?.filter(c => c.columnIndex === columnIndex - 1)
        .reduce((count, connector) => {
          const task: Task = connector.sourceItem.data.task;
          const stateNode = getWorkflowState(task);
          if (stateNode?.type !== 'final') {
            count++;
          }
          count += countLinkedItemsRequiresReview(
            connector.sourceItem,
            connector.columnIndex
          );
          return count;
        }, 0) || 0
    );
  };

  const renderTitleIcon = ({
    item,
    isSelected,
    isCollapsed
  }: RenderOptions) => {
    if (!item.data?.task) return null;
    const task: Task = item.data.task;
    if (!isCollapsed) return null;
    return <TaskStatusDot task={task} />;
  };
  const renderTitleAccessory = ({
    columnIndex,
    item,
    isCollapsed
  }: RenderOptions) => {
    if (!item.data?.task) return null;
    const task: Task = item.data.task;
    if (isCollapsed) return null;
    // find an item in the row that is not in the final state
    const stateNode = getWorkflowState(task);
    const requiresReviewCount =
      stateNode?.type === 'final'
        ? countLinkedItemsRequiresReview(item, columnIndex)
        : 0;
    return (
      <>
        {requiresReviewCount ? (
          <Tooltip
            title={`${requiresReviewCount} upstream linked item(s) that requires review`}
            placement='top'
            arrow
          >
            <StyledWarning />
          </Tooltip>
        ) : null}
        <TaskStatusLabel size='small' task={task} />
      </>
    );
  };
  const renderSubContent = ({
    item,
    column,
    columnIndex,
    isSelected,
    isCollapsed
  }: RenderOptions) => {
    let path = '';
    if (!item.data) return null;
    if (isSelected) {
      setHazardId(item.data.hazardId || item.data.id);
    }
    const hazardId = item.data.hazardId || item.data.id;
    switch (columnIndex) {
      case 0:
        path = `/portal/products/${product.id}/hazards/${hazardId}`;
        break;
      case 1:
        path = `/portal/products/${product.id}/hazards/${hazardId}/causes/${item.id}?from=hazardTrace`;
        break;
      case 2:
        path = `/portal/products/${product.id}/hazards/${hazardId}/controls/${item.id}?from=hazardTrace`;
        break;
      case 3:
        path = `/portal/products/${product.id}/hazards/${hazardId}/actions/${item.id}?from=hazardTrace`;
        break;
    }
    if (isCollapsed) return null;
    const task = item.data?.task as Task;
    return (
      <StyledSubContent>
        {task?.user && (
          <div className='assigned'>
            <div className='icon-wrapper'>
              <Person className='icon' />
            </div>
            {task.user.firstName} {task.user.lastName}
          </div>
        )}
        <div className={`view-details${isSelected ? '' : ' hover-only'}`}>
          <Button
            variant='outlined'
            color='primary'
            className='button'
            component={Link}
            to={path}
          >
            View Details
          </Button>
        </div>
      </StyledSubContent>
    );
  };
  const { columns } = useMemo(() => {
    const query = `from=hazardTrace${
      hasFilters ? `&${queryString.encode(apiParams || {})}` : ''
    }`;
    // wait till hazardBoards is finished loading to prevent display of
    // empty state message
    if (isLoading || !loadedHazardBoard) return { columns: [] };
    const columns = [
      {
        title: 'Hazard',
        headerLink: `./hazard?${query}`,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items:
          loadedHazardBoard?.hazards.map(hazardItem => ({
            id: hazardItem.id,
            title: `${getHazardIdPrefix()}-${hazardItem.id}`,
            description: hazardItem.title,
            data: hazardItem
          })) || []
      },
      {
        title: 'Causes',
        headerLink: `./causes?${query}`,
        disableAdd: true,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items:
          loadedHazardBoard?.causes.map(cause => ({
            id: cause.id,
            title: `CAUSE-${cause.id}`,
            description: cause.title,
            data: cause
          })) || []
      },
      {
        title: 'Controls',
        headerLink: `./controls?${query}`,
        disableAdd: true,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items:
          loadedHazardBoard?.controls.map(control => ({
            id: control.id,
            title: `CONTROL-${control.id}`,
            description: control.title,
            data: control
          })) || []
      },
      {
        title: 'Actions',
        headerLink: `./actions?${query}`,
        disableAdd: true,
        renderColumnMenu,
        renderTitleIcon,
        renderTitleAccessory,
        renderSubContent,
        items:
          loadedHazardBoard?.actions.map(action => ({
            id: action.id,
            title: `ACTION-${action.id}`,
            description: action.title,
            data: action
          })) || []
      }
    ];

    return { columns };
  }, [loadedHazardBoard]);

  const getWorkflowState = (task: Task | undefined) => {
    return getStateNodeByStatus(task?.status, getWorkflow(task));
  };

  return (
    <TraceBoard
      innerRef={traceBoardRef}
      heading={hasFilters ? 'Filtered Cards' : undefined}
      orphanedRowHeading={hasFilters ? 'Orphaned Cards from filter' : undefined}
      columns={columns}
      isLoading={isLoading}
      filterColumnItems={(primaryItem, sourceItem, column, columnIndex) => {
        const data = sourceItem?.data;
        switch (columnIndex) {
          // causes
          case 1: {
            if (!data) return [];
            const hazards = data as Hazard;
            return column.items.filter(item =>
              hazards.causes?.some(c => c.id === item.id)
            );
          }
          // controls
          case 2: {
            if (!data) return [];
            const cause = data as HazardCause;
            return column.items.filter(item =>
              cause.controls?.some(c => c.id === item.id)
            );
          }
          // actions
          case 3: {
            if (!data) return [];
            const control = data as HazardControl;
            return column.items.filter(item =>
              control.actions?.some(a => a.id === item.id)
            );
          }
          default:
            return [];
        }
      }}
      filterOrphanedItems={(column, columnIndex) => {
        if (!loadedHazardBoard) return [];
        switch (columnIndex) {
          // controls
          case 2: {
            return column.items.filter(
              item =>
                (item.id === 'new' ||
                  loadedHazardBoard.hazards.find(
                    hazard => hazard.id === item.data?.hazardId
                  )) &&
                !loadedHazardBoard.causes.some(c =>
                  c.controls?.some(control => control.id === item.id)
                )
            );
          }
          // actions
          case 3: {
            return column.items.filter(item => {
              // only get items in current product board
              if (
                item.id !== 'new' &&
                !loadedHazardBoard.hazards.find(
                  hazard => hazard.id === item.data?.hazardId
                )
              ) {
                return false;
              }
              // get all related controls
              const controls = loadedHazardBoard?.controls.filter(control =>
                control.actions?.some(action => action.id === item.id)
              );
              // see if any of the related controls does not have a cause
              return !loadedHazardBoard?.causes.some(c =>
                c.controls?.some(cc => controls?.some(c => c.id === cc.id))
              );
            });
          }
          default:
            return [];
        }
      }}
      canEditItem={sourceItem => {
        const stateNode = getWorkflowState(sourceItem.data?.task);
        if (!stateNode) return;
        const allowedFields = stateNode.meta?.allowedFields || [];
        return allowedFields.includes('title');
      }}
      canLinkNewItem={(sourceItem, column, columnIndex) => {
        return columnIndex !== 3;
      }}
      onAddItem={(newItem, sourceItem, column, columnIndex) => {
        const sourceItemIds = sourceItem ? [sourceItem.id] : [];
        console.log(newItem);
        switch (columnIndex) {
          case 0:
            createProductHazard({ title: newItem.description });
            break;
          case 1:
            createHazardCause({ title: newItem.description });
            break;
          case 2:
            createHazardControl({
              title: newItem.description,
              causeIds: sourceItemIds
            });
            break;
          case 3:
            createHazardAction({
              title: newItem.description,
              controlIds: sourceItemIds
            });
            break;
        }
      }}
      onUpdateItem={(item, column, columnIndex) => {
        console.log(item);
        switch (columnIndex) {
          case 0:
            updateHazard({ id: item.data.id, title: item.description });
            break;
          case 1:
            updateHazardCause({
              id: item.data.id,
              title: item.description
            });
            break;
          case 2:
            updateHazardControl({
              id: item.data.id,
              title: item.description
            });
            break;
          case 3:
            updateHazardAction({
              id: item.data.id,
              title: item.description
            });
            break;
        }
      }}
    />
  );
};

export default ProductHazardBoardTrace;
